import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Tabs, Tab, Paper, CircularProgress,
  TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Autocomplete
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaidIcon from '@mui/icons-material/Paid';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useAuth } from 'react-oidc-context';
import { useSession, Terminal, Equipo } from '../../../../core/context/SessionContext';
import { SaldosApiService } from '../../infrastructure/api/SaldosApiService';
import { VentaApiService } from '../../../venta/infrastructure/api/VentaApiService';
import { TipoRecaudo } from '../../domain/entities/TipoRecaudo';
import { ResumenHoy } from '../../domain/entities/ResumenHoy';

const formatCurrency = (value: string) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));

const today = new Date().toISOString().split('T')[0];


export const SaldosPage: React.FC = () => {
  const auth = useAuth();
  const { terminal, equipo } = useSession();
  const esDG = terminal?.codigo_terminal === 10;
  const [tabActiva, setTabActiva] = useState(0);

  // --- Filtros terminal/equipo (solo DG) ---
  const [terminales, setTerminales] = useState<Terminal[]>([]);
  const [equiposDG, setEquiposDG] = useState<Equipo[]>([]);
  const [selectedTerminal, setSelectedTerminal] = useState<Terminal | null>(null);
  const [selectedEquipo, setSelectedEquipo] = useState<Equipo | null>(null);

  // --- Tab Bolsillo ---
  const [saldoEfectivo, setSaldoEfectivo] = useState<number | null>(null);
  const [resumenHoy, setResumenHoy] = useState<ResumenHoy | null>(null);
  const [isLoadingBolsillo, setIsLoadingBolsillo] = useState(false);
  const [errorBolsillo, setErrorBolsillo] = useState<string | null>(null);

  const totalTarjeta = resumenHoy
    ? Math.round((resumenHoy.tarjetaCredito.total + resumenHoy.tarjetaDebito.total) * 100) / 100
    : null;

  const totalEquipo =
    saldoEfectivo !== null && totalTarjeta !== null
      ? Math.round((saldoEfectivo + totalTarjeta) * 100) / 100
      : null;

  useEffect(() => {
    if (tabActiva !== 0) return;
    const cargar = async () => {
      if (!auth.user?.access_token || !equipo || !terminal) return;
      setIsLoadingBolsillo(true);
      setErrorBolsillo(null);
      try {
        const [bolsillo, resumen] = await Promise.all([
          SaldosApiService.obtenerBolsilloEquipo(
            equipo.codigo_equipo,
            terminal.codigo_terminal,
            auth.user.access_token,
            terminal.pais
          ),
          SaldosApiService.obtenerResumenHoy(
            equipo.codigo_equipo,
            terminal.codigo_terminal,
            auth.user.access_token,
            terminal.pais
          ),
        ]);
        setSaldoEfectivo(Number(bolsillo.saldoBolsillo));
        setResumenHoy(resumen);
      } catch (err) {
        setErrorBolsillo(err instanceof Error ? err.message : 'Error desconocido');
      } finally {
        setIsLoadingBolsillo(false);
      }
    };
    cargar();
  }, [tabActiva, auth.user?.access_token, equipo, terminal]);

  // --- Tab Histórico ---
  const [fechaInicial, setFechaInicial] = useState(today);
  const [fechaFinal, setFechaFinal] = useState(today);
  const [busquedaVersion, setBusquedaVersion] = useState(0);
  const [registros, setRegistros] = useState<TipoRecaudo[]>([]);
  const [isLoadingHistorico, setIsLoadingHistorico] = useState(false);
  const [errorHistorico, setErrorHistorico] = useState<string | null>(null);

  const totalHistorico = useMemo(
    () => registros.reduce((acc, item) => acc + Math.round(Number(item.valor) * 100), 0) / 100,
    [registros]
  );

  useEffect(() => {
    if (tabActiva !== 1) return;
    const cargar = async () => {
      const codigoTerminal = esDG ? selectedTerminal?.codigo_terminal : terminal?.codigo_terminal;
      const codigoEquipo   = esDG ? selectedEquipo?.codigo_equipo    : equipo?.codigo_equipo;
      if (!auth.user?.access_token || codigoTerminal === undefined || codigoEquipo === undefined) {
        if (esDG) setRegistros([]);
        return;
      }
      setIsLoadingHistorico(true);
      setErrorHistorico(null);
      try {
        const data = await SaldosApiService.obtenerTipoRecaudo(
          codigoEquipo,
          codigoTerminal,
          fechaInicial,
          fechaFinal,
          auth.user.access_token,
          terminal?.pais ?? ''
        );
        setRegistros(data);
      } catch (err) {
        setErrorHistorico(err instanceof Error ? err.message : 'Error desconocido');
      } finally {
        setIsLoadingHistorico(false);
      }
    };
    cargar();
  }, [fechaInicial, fechaFinal, busquedaVersion, tabActiva, auth.user?.access_token, equipo, terminal, esDG, selectedTerminal, selectedEquipo]);

  // Carga terminales (solo DG)
  useEffect(() => {
    if (!esDG || !auth.user?.access_token) return;
    VentaApiService.obtenerTerminalesBD(auth.user.access_token, terminal?.pais)
      .then(setTerminales)
      .catch(() => {});
  }, [esDG, auth.user?.access_token, terminal?.pais]);

  // Carga equipos al seleccionar terminal (solo DG)
  useEffect(() => {
    if (!selectedTerminal || !auth.user?.access_token) {
      setEquiposDG([]);
      setSelectedEquipo(null);
      return;
    }
    VentaApiService.obtenerEquiposBD(selectedTerminal.codigo_terminal, auth.user.access_token, terminal?.pais)
      .then(setEquiposDG)
      .catch(() => {});
  }, [selectedTerminal, auth.user?.access_token, terminal?.pais]);

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
        Saldos del Equipo
      </Typography>

      <Tabs
        value={tabActiva}
        onChange={(_, v: number) => setTabActiva(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        <Tab label="Bolsillo" />
        <Tab label="Histórico" />
      </Tabs>

      {/* ── Tab Bolsillo ── */}
      {tabActiva === 0 && (
        <Box>
          {isLoadingBolsillo && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!isLoadingBolsillo && errorBolsillo && (
            <Typography color="error" sx={{ mt: 2 }}>{errorBolsillo}</Typography>
          )}
          {!isLoadingBolsillo && !errorBolsillo && totalEquipo !== null && (
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {[
                { label: 'Pendiente por Legalizar Equipo', value: totalEquipo, Icon: AccountBalanceWalletIcon },
                { label: 'Pendiente por Legalizar Tarjeta', value: totalTarjeta ?? 0, Icon: CreditCardIcon },
                { label: 'Pendiente por Legalizar Efectivo', value: saldoEfectivo ?? 0, Icon: PaidIcon },
              ].map(({ label, value, Icon }) => (
                <Paper
                  key={label}
                  elevation={3}
                  sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 2, minWidth: 240 }}
                >
                  <Box
                    sx={{
                      backgroundColor: '#e3f2fd',
                      borderRadius: '50%',
                      width: 56,
                      height: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ color: '#0066cc', fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {label}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0066cc' }}>
                      {formatCurrency(String(value))}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* ── Tab Histórico ── */}
      {tabActiva === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
          <Paper elevation={1} sx={{ p: 2, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                label="Fecha Inicial"
                type="date"
                size="small"
                value={fechaInicial}
                onChange={(e) => setFechaInicial(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                label="Fecha Final"
                type="date"
                size="small"
                value={fechaFinal}
                onChange={(e) => setFechaFinal(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />

              {esDG && (
                <>
                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 220 }}
                    options={terminales}
                    value={selectedTerminal}
                    getOptionLabel={(t) => `${t.nombre_terminal} (Cód: ${t.codigo_terminal})`}
                    filterOptions={(opts, { inputValue }) => {
                      const q = inputValue.toLowerCase();
                      return opts.filter(t =>
                        t.nombre_terminal.toLowerCase().includes(q) ||
                        String(t.codigo_terminal).includes(q)
                      );
                    }}
                    onChange={(_, val) => {
                      setSelectedTerminal(val);
                      setSelectedEquipo(null);
                    }}
                    isOptionEqualToValue={(opt, val) => opt.codigo_terminal === val.codigo_terminal}
                    renderInput={(params) => <TextField {...params} label="Terminal" />}
                  />
                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 180 }}
                    options={equiposDG}
                    value={selectedEquipo}
                    disabled={!selectedTerminal}
                    getOptionLabel={(e) => e.nombre_equipo}
                    filterOptions={(opts, { inputValue }) => {
                      const q = inputValue.toLowerCase();
                      return opts.filter(e => e.nombre_equipo.toLowerCase().includes(q));
                    }}
                    onChange={(_, val) => setSelectedEquipo(val)}
                    isOptionEqualToValue={(opt, val) => opt.codigo_equipo === val.codigo_equipo}
                    renderInput={(params) => <TextField {...params} label="Equipo" />}
                  />
                </>
              )}
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => setBusquedaVersion((v) => v + 1)}
                disabled={isLoadingHistorico || (esDG && (!selectedTerminal || !selectedEquipo))}
                title={esDG && (!selectedTerminal || !selectedEquipo) ? 'Selecciona una terminal y un equipo para filtrar' : undefined}
              >
                Filtrar
              </Button>
              {esDG && (selectedTerminal || selectedEquipo) && (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<ClearIcon />}
                  onClick={() => {
                    setSelectedTerminal(null);
                    setSelectedEquipo(null);
                    setBusquedaVersion((v) => v + 1);
                  }}
                  disabled={isLoadingHistorico}
                >
                  Limpiar
                </Button>
              )}
            </Box>
            {registros.length > 0 && (
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary">Total período</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0066cc', lineHeight: 1.2 }}>
                  {formatCurrency(String(totalHistorico))}
                </Typography>
              </Box>
            )}
          </Paper>

          {isLoadingHistorico && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
              <CircularProgress />
            </Box>
          )}
          {!isLoadingHistorico && errorHistorico && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
              <Typography color="error">{errorHistorico}</Typography>
            </Box>
          )}
          {!isLoadingHistorico && !errorHistorico && (
            <TableContainer component={Paper} elevation={3} sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto', borderRadius: 2 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Descripción</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '200px' }}>Valor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {registros.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ py: 5 }}>
                        <Typography color="text.secondary">
                          No hay registros para el período seleccionado.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    registros.map((item, index) => (
                      <TableRow key={`${item.identificador_recurso}-${index}`} hover>
                        <TableCell>{item.identificador_recurso}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(item.valor)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
};
